import { blockCategories } from '../../categories';

const BUTTON_TYPE_CLASS_MAP = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    destructive: 'btn-danger',
    'primary-outline': 'btn-outline-primary',
    'secondary-outline': 'btn-outline-secondary',
    ghost: 'btn-link',
};

const BUTTON_SIZE_CLASS_MAP = {
    standard: 'btn-md',
    small: 'btn-sm',
    large: 'btn-lg',
};

const MANAGED_BUTTON_CLASSES = [
    'sio-btn',
    ...Object.values(BUTTON_TYPE_CLASS_MAP),
    ...Object.values(BUTTON_SIZE_CLASS_MAP).filter(Boolean),
];

export default (editor) => {
    const blockManager = editor.BlockManager;
    const domComponents = editor.DomComponents;

    domComponents.addType('sio-button', {
        isComponent: (el) => {
            if (el?.tagName === 'A' && el.classList?.contains('sio-btn')) {
                return { type: 'sio-button' };
            }

            return undefined;
        },
        model: {
            defaults: {
                tagName: 'a',
                draggable: true,
                droppable: false,
                editable: true,
                content: 'Button',
                attributes: {
                    href: '',
                },
                classes: ['sio-btn', 'btn-primary'],
                'button-title': 'Button',
                href: '',
                rel: '',
                target: '',
                'button-type': 'primary',
                'button-size': 'standard',
                traits: [
                    {
                        type: 'text',
                        label: 'Titel des Buttons',
                        name: 'button-title',
                        changeProp: 1,
                    },
                    {
                        type: 'text',
                        label: 'Link',
                        name: 'href',
                        placeholder: '#',
                        changeProp: 1,
                    },

                    {
                        type: 'select',
                        label: 'Typ',
                        name: 'button-type',
                        options: [
                            { value: 'primary', name: 'Primary' },
                            { value: 'secondary', name: 'Secondary' },
                            { value: 'destructive', name: 'Destructive' },
                            { value: 'primary-outline', name: 'Primary Outline' },
                            { value: 'secondary-outline', name: 'Secondary Outline' },
                            { value: 'ghost', name: 'Ghost' },
                        ],
                        changeProp: 1,
                    },
                    {
                        type: 'select',
                        label: 'Größe',
                        name: 'button-size',
                        options: [
                            { value: 'standard', name: 'Standard' },
                            { value: 'small', name: 'Klein' },
                            { value: 'large', name: 'Groß' },
                        ],
                        changeProp: 1,
                    },
                    {
                        type: 'select',
                        label: 'Target',
                        name: 'target',
                        options: [
                            { value: '', name: 'Dieses Fenster' },
                            { value: '_blank', name: 'Neues Fenster' },
                        ],
                        changeProp: 1,
                    },
                    {
                        type: 'select',
                        label: 'Rel',
                        name: 'rel',
                        options: [
                            { value: '', name: '-----' },
                            { value: 'unfollow', name: 'Unfollow' },
                            { value: 'tag', name: 'Tag' },
                        ],
                        changeProp: 1,
                    },
                ],
            },
            init() {
                this.on('change:button-title', this.handleTitleChange);
                this.on('change:content', this.syncTitleTrait);
                this.on('change:href', this.handleHrefChange);
                this.on('change:rel', this.handleRelChange);
                this.on('change:target', this.handleTargetChange);
                this.on('change:button-type', this.handleButtonClassChange);
                this.on('change:button-size', this.handleButtonClassChange);
                this.listenTo(this.components(), 'add remove change:content', this.syncTitleTrait);

                const attrs = this.getAttributes();
                const classes = this.getClasses();

                const detectedType =
                    Object.entries(BUTTON_TYPE_CLASS_MAP).find(([, className]) =>
                        classes.includes(className)
                    )?.[0] || this.get('button-type') || 'primary';

                const detectedSize =
                    Object.entries(BUTTON_SIZE_CLASS_MAP).find(
                        ([, className]) => className && classes.includes(className)
                    )?.[0] || this.get('button-size') || 'standard';

                this.set(
                    {
                        'button-title': this.get('content') || this.get('button-title') || 'Button',
                        href: attrs.href || this.get('href') || '#',
                        rel: attrs.rel || this.get('rel') || '',
                        target: attrs.target || this.get('target') || '',
                        'button-type': detectedType,
                        'button-size': detectedSize,
                    },
                    { silent: true }
                );

                this.handleTitleChange();
                this.handleHrefChange();
                this.handleRelChange();
                this.handleTargetChange();
                this.handleButtonClassChange();
            },
            handleTitleChange() {
                const buttonTitle = this.get('button-title') || 'Button';
                this.set('content', buttonTitle);
                if (this.components().length !== 1 || this.components().models[0]?.get('content') !== buttonTitle) {
                    this.components(buttonTitle);
                }
            },
            syncTitleTrait() {
                const text = this.components().models.map(c => c.get('content') || '').join('');
                if (text !== this.get('button-title')) {
                    this.set('button-title', text, { silent: true });
                }
            },
            handleHrefChange() {
                const href = this.get('href') || '#';
                this.addAttributes({ href });
            },
            handleRelChange() {
                const rel = this.get('rel');
                const attrs = { ...this.getAttributes() };

                if (rel) {
                    attrs.rel = rel;
                } else {
                    delete attrs.rel;
                }

                this.setAttributes(attrs);
            },
            handleTargetChange() {
                const target = this.get('target');
                const attrs = { ...this.getAttributes() };

                if (target) {
                    attrs.target = target;
                } else {
                    delete attrs.target;
                }

                this.setAttributes(attrs);
            },
            handleButtonClassChange() {
                MANAGED_BUTTON_CLASSES.forEach((className) => {
                    this.removeClass(className);
                });

                this.addClass('sio-btn');

                const typeClass =
                    BUTTON_TYPE_CLASS_MAP[this.get('button-type')] ||
                    BUTTON_TYPE_CLASS_MAP.primary;
                const sizeClass = BUTTON_SIZE_CLASS_MAP[this.get('button-size')] || '';

                if (typeClass) {
                    this.addClass(typeClass);
                }

                if (sizeClass) {
                    this.addClass(sizeClass);
                }
            },
        },
    });

    blockManager.add('sio-button', {
        label: 'Button',
        category: blockCategories.basic,
        content: {
            type: 'sio-button',
        },
        media: `<svg fill="#000000" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M2 7C0.906937 7 0 7.906937 0 9V15C0 16.0931 0.906937 17 2 17H22C23.0931 17 24 16.0931 24 15V9C24 7.906937 23.0931 7 22 7H2ZM2 9H22V15H2V9ZM7 11V13H9V11H7ZM11 11V13H13V11H11ZM15 11V13H17V11H15Z"/></svg>`,
    });
};
